import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Button,
  Checkbox,
  DateField,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FileUploader,
  Label,
  Price,
  Textarea,
} from '@hero/web-components';
import { fileToBase64 } from '@hero/web-utils';
import {
  AlertCircleIcon,
  CheckIcon,
  FileTextIcon,
} from '@hero/web-icons';
import type { FinancingLenderView } from '@hero/api';
import type { Currency } from '@hero/schema';

/**
 * Repayment-write callback used by the direct-record path (lender
 * has no carrier receipt to review and is recording the event
 * themselves) and the review path's "approve" action (carrier
 * receipt exists; lender confirms / corrects the date).
 *
 * `repaymentProof` is intentionally optional: the review path
 * reuses `application.repaymentReceipt.image` server-side and
 * never re-uploads. The direct-record path always supplies a
 * fresh upload at the SPA layer.
 */
export type LenderRepaymentApproveInput = {
  at: Date;
  repaymentProof?: { upload: string };
  notes?: string;
};

/** Reject-write callback signature for the review path. */
export type LenderRepaymentRejectInput = { notes: string };

export type LenderRepaymentPanelProps = {
  view: FinancingLenderView;
  /**
   * Approve / record-repayment write. Wired to
   * `lenderMarkRepaidAPI` in the parent — same endpoint covers
   * both the direct-record (status: `funded`) and the approval
   * (status: `awaiting_repayment_approval`) paths.
   */
  onConfirmRepayment: (input: LenderRepaymentApproveInput) => Promise<void>;
  /**
   * Reject-write for the review path. Wired to
   * `lenderRejectRepaymentAPI` in the parent. The dialog launched
   * from this panel enforces a non-empty `notes` so the carrier
   * sees a concrete reason on retry.
   */
  onRejectRepayment: (input: LenderRepaymentRejectInput) => Promise<void>;
};

/**
 * `LenderRepaymentPanel` — the lender's post-funding "I got my
 * money back" surface on the public lender-view page. The panel
 * is context-aware based on `application.status`:
 *
 *  - **`'awaiting_repayment_approval'`** — the carrier has
 *    submitted a {@link FinancingApplication.repaymentReceipt} and
 *    is waiting on the lender's verification. The panel renders
 *    the carrier's claim (image link, claimed `repaidAt`, notes,
 *    `submittedAt`) and lets the lender Approve (with an editable
 *    date) or Reject (with a required reason).
 *
 *    On approve we reuse the carrier's already-uploaded image
 *    server-side — no second proof upload from the lender. The
 *    same endpoint handles both paths so the audit trail is
 *    uniform.
 *
 *  - **`'funded'`** — the carrier hasn't submitted a receipt; the
 *    lender already has the money off-platform and records the
 *    repayment with their own proof. This is the original direct-
 *    record path. We keep it as a fallback per pilot reality
 *    (some carriers still don't open the dashboard to submit a
 *    receipt; the lender wants to close the loop regardless).
 *
 * Visibility (decided by the parent page) — the panel mounts only
 * while the lender's offer is `accepted` and the status is
 * `awaiting_repayment_approval` or `funded`. The `'repaid'` state
 * swaps in the read-only {@link LenderRepaymentRecordedCard}.
 */
export function LenderRepaymentPanel({
  view,
  onConfirmRepayment,
  onRejectRepayment,
}: LenderRepaymentPanelProps) {
  const { application } = view;
  const reviewMode =
    application.status === 'awaiting_repayment_approval' &&
    Boolean(application.repaymentReceipt);
  return reviewMode ? (
    <ReviewMode
      view={view}
      onApprove={onConfirmRepayment}
      onReject={onRejectRepayment}
    />
  ) : (
    <DirectRecordMode view={view} onConfirm={onConfirmRepayment} />
  );
}

/* ─────────────────────── review mode ─────────────────────── */

/**
 * Review mode — the carrier already submitted a receipt and the
 * lender is verifying it. Shows the carrier's claim verbatim
 * (image, notes, claimed date, submittedAt) and lets the lender
 * either approve (with a possibly-corrected date) or reject (with
 * a required reason that the carrier will see on retry).
 */
function ReviewMode({
  view,
  onApprove,
  onReject,
}: {
  view: FinancingLenderView;
  onApprove: (input: LenderRepaymentApproveInput) => Promise<void>;
  onReject: (input: LenderRepaymentRejectInput) => Promise<void>;
}) {
  const { t, i18n } = useTranslation('m-financing');
  const { application } = view;
  const { myOffer, invoice, outcome, repaymentReceipt } = application;
  const terms = myOffer.terms;
  const repaymentTotal = terms ? terms.advancedAmount + terms.feeAmount : 0;
  /**
   * The wire schema types `invoice.currency` as `z.string()` (it
   * predates the `Currency` enum being shared), but at runtime
   * every financing application is seeded with `'SAR'`. Cast once
   * here so the rest of the file can pass it to `<Price>` without
   * repeated `as Currency` noise.
   */
  const currency = invoice.currency as Currency;

  /**
   * Prefer the carrier's claimed `repaidAt`, fall back to the
   * server-stamped `submittedAt`, fall back to today. The lender
   * can still override before approving.
   */
  const [repaidOn, setRepaidOn] = useState<Date | undefined>(() => {
    if (repaymentReceipt?.repaidAt) return new Date(repaymentReceipt.repaidAt);
    if (repaymentReceipt?.submittedAt)
      return new Date(repaymentReceipt.submittedAt);
    return new Date();
  });
  const [acknowledged, setAcknowledged] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const canApprove = Boolean(repaidOn) && acknowledged && !approving;

  const handleApprove = async () => {
    if (!repaidOn) return;
    setApproving(true);
    try {
      // No repaymentProof — backend reuses receipt.image.
      await onApprove({ at: repaidOn });
      toast.success(t('partner_page.repayment.toasts.success'));
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t('partner_page.repayment.toasts.failed'),
      );
    } finally {
      setApproving(false);
    }
  };

  return (
    <section className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-5 shadow-sm sm:p-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-base font-semibold text-gray-900">
          {t('partner_page.repayment.review.title')}
        </h2>
        <p className="text-sm text-gray-600">
          {t('partner_page.repayment.review.description')}
        </p>
      </div>

      <div className="mt-5">
        <LockedRepaymentTermsCard
          terms={terms}
          currency={currency}
          fundedAt={outcome?.fundedAt}
        />
      </div>

      {/* Carrier's submission, verbatim */}
      <div className="mt-5 rounded-xl border border-emerald-200 bg-white p-4">
        <div className="flex items-center gap-2">
          <FileTextIcon className="h-4 w-4 text-emerald-600" />
          <h3 className="text-sm font-semibold text-gray-900">
            {t('partner_page.repayment.review.carrier_submission.title')}
          </h3>
        </div>
        <p className="mt-0.5 text-xs text-gray-500">
          {t('partner_page.repayment.review.carrier_submission.subtitle')}
        </p>
        <dl className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
          <SubmissionRow
            label={t('partner_page.repayment.review.fields.claimed_repaid_on')}
            value={
              repaymentReceipt?.repaidAt
                ? formatDate(repaymentReceipt.repaidAt, i18n.language)
                : t('partner_page.repayment.review.fields.not_provided')
            }
          />
          <SubmissionRow
            label={t('partner_page.repayment.review.fields.submitted_at')}
            value={formatDate(
              repaymentReceipt?.submittedAt,
              i18n.language,
            )}
          />
          {repaymentReceipt?.imageUrl ? (
            <SubmissionRow
              label={t('partner_page.repayment.review.fields.proof')}
              value={
                <a
                  href={repaymentReceipt.imageUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-emerald-700 underline">
                  {t('partner_page.repayment.review.fields.open_proof')}
                </a>
              }
              span={2}
            />
          ) : (
            <SubmissionRow
              label={t('partner_page.repayment.review.fields.proof')}
              value={t('partner_page.repayment.review.fields.no_proof')}
              span={2}
            />
          )}
          {repaymentReceipt?.notes && (
            <SubmissionRow
              label={t('partner_page.repayment.review.fields.notes')}
              value={
                <span className="whitespace-pre-wrap text-gray-700">
                  {repaymentReceipt.notes}
                </span>
              }
              span={2}
            />
          )}
        </dl>
      </div>

      {/* Lender confirms/corrects the date */}
      <div className="mt-5">
        <DateField
          label={t('partner_page.repayment.review.fields.confirm_repaid_on')}
          required
          value={repaidOn}
          onChange={setRepaidOn}
        />
        <p className="mt-1 text-xs text-gray-500">
          {t('partner_page.repayment.review.fields.confirm_repaid_on_hint')}
        </p>
      </div>

      <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-xl border border-emerald-200 bg-white p-4">
        <Checkbox
          className="mt-0.5"
          checked={acknowledged}
          onCheckedChange={value => setAcknowledged(value === true)}
        />
        <span className="text-sm text-gray-700">
          {t('partner_page.repayment.review.acknowledge')}
        </span>
      </label>

      <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
        <Button
          type="button"
          variant="destructive"
          onClick={() => setRejectOpen(true)}
          className="gap-2">
          {t('partner_page.repayment.review.actions.reject')}
        </Button>
        <Button
          type="button"
          disabled={!canApprove}
          onClick={handleApprove}
          className="gap-2">
          {approving ? (
            t('partner_page.repayment.review.actions.approving')
          ) : (
            <>
              {t('partner_page.repayment.review.actions.approve')}
              {terms && (
                <Price
                  amount={repaymentTotal}
                  currency={currency}
                  size="sm"
                  className="font-semibold"
                />
              )}
            </>
          )}
        </Button>
      </div>

      <RejectRepaymentDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        onSubmit={onReject}
      />
    </section>
  );
}

function SubmissionRow({
  label,
  value,
  span,
}: {
  label: string;
  value: ReactNode;
  span?: 2;
}) {
  return (
    <div
      className={`flex flex-col gap-0.5 rounded-md bg-gray-50 px-3 py-2 ${
        span === 2 ? 'sm:col-span-2' : ''
      }`}>
      <dt className="text-[11px] uppercase tracking-wide text-gray-500">
        {label}
      </dt>
      <dd className="text-sm text-gray-900">{value}</dd>
    </div>
  );
}

/* ─────────────────────── reject dialog ─────────────────────── */

function RejectRepaymentDialog({
  open,
  onOpenChange,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: { notes: string }) => Promise<void>;
}) {
  const { t } = useTranslation('m-financing');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = notes.trim().length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onSubmit({ notes: notes.trim() });
      toast.success(t('partner_page.repayment.review.toasts.reject_success'));
      onOpenChange(false);
      setNotes('');
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t('partner_page.repayment.review.toasts.reject_failed'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t('partner_page.repayment.review.reject_dialog.title')}
          </DialogTitle>
          <DialogDescription>
            {t('partner_page.repayment.review.reject_dialog.description')}
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
          <p className="text-xs text-red-700">
            {t('partner_page.repayment.review.reject_dialog.warning')}
          </p>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="flex items-center gap-1">
            {t('partner_page.repayment.review.reject_dialog.notes_label')}
            <span className="text-red-600" aria-hidden="true">
              *
            </span>
          </Label>
          <Textarea
            rows={4}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder={t(
              'partner_page.repayment.review.reject_dialog.notes_placeholder',
            )}
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}>
            {t('partner_page.repayment.review.reject_dialog.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={!canSubmit}
            onClick={handleSubmit}>
            {submitting
              ? t('partner_page.repayment.review.reject_dialog.submitting')
              : t('partner_page.repayment.review.reject_dialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────── direct-record mode ─────────────────────── */

/**
 * Direct-record mode — the carrier hasn't submitted a receipt but
 * the lender already received the money off-platform. The lender
 * records the repayment with their own proof. Same gating logic
 * as the original v0 panel:
 *
 *  - **Locked terms** are still the headline. The lender's gross
 *    margin is `(advance + fee) - advance = fee`, but we render
 *    "Total received" prominently because that's the wire size the
 *    bank statement will show. No editing — the offer is locked at
 *    acceptance.
 *  - **Operational inputs**: `repaidOn` (date picker, defaults to
 *    today), required `repaymentProof` upload, optional notes.
 *  - **Acknowledgement** mirrors the funding panel — keeps the
 *    lender deliberate about closing the lifecycle.
 *
 * Note on `outcome.notes`: when the panel opens after a previous
 * rejection (status looped funded → awaiting → funded), the
 * carrier sees the rejection reason on their side; the lender's
 * own previous rejection is the same `outcome.notes` value. We
 * don't surface it here — by the time the lender lands back on
 * the direct-record path, they're consciously closing the loop
 * with their own proof rather than reviewing a carrier receipt.
 */
function DirectRecordMode({
  view,
  onConfirm,
}: {
  view: FinancingLenderView;
  onConfirm: (input: LenderRepaymentApproveInput) => Promise<void>;
}) {
  const { t } = useTranslation('m-financing');
  const { application } = view;
  const { myOffer, invoice, outcome } = application;
  const terms = myOffer.terms;
  // See `ReviewMode` for the rationale on this cast.
  const currency = invoice.currency as Currency;

  const [repaidOn, setRepaidOn] = useState<Date | undefined>(() => new Date());
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [notes, setNotes] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit =
    Boolean(repaidOn) &&
    Boolean(proofFile) &&
    acknowledged &&
    !submitting;

  const handleSubmit = async () => {
    if (!repaidOn || !proofFile) return;
    setSubmitting(true);
    try {
      const repaymentProof = { upload: await fileToBase64(proofFile) };
      await onConfirm({
        at: repaidOn,
        repaymentProof,
        notes: notes.trim() ? notes.trim() : undefined,
      });
      toast.success(t('partner_page.repayment.toasts.success'));
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t('partner_page.repayment.toasts.failed'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const repaymentTotal = terms ? terms.advancedAmount + terms.feeAmount : 0;

  return (
    <section className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-5 shadow-sm sm:p-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-base font-semibold text-gray-900">
          {t('partner_page.repayment.title')}
        </h2>
        <p className="text-sm text-gray-600">
          {t('partner_page.repayment.description')}
        </p>
      </div>

      <div className="mt-5">
        <LockedRepaymentTermsCard
          terms={terms}
          currency={currency}
          fundedAt={outcome?.fundedAt}
        />
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <DateField
          label={t('partner_page.repayment.fields.repaid_on.title')}
          required
          value={repaidOn}
          onChange={setRepaidOn}
        />
        <div className="flex flex-col gap-1.5">
          <Label className="flex items-center gap-1">
            {t('partner_page.repayment.fields.proof.title')}
            <span className="text-red-600" aria-hidden="true">
              *
            </span>
          </Label>
          <FileUploader
            files={proofFile ? [proofFile] : []}
            onChange={files => setProofFile(files[0] ?? null)}
            label={t('partner_page.repayment.fields.proof.label')}
            variant="compact"
            acceptedFileTypes={{
              'application/pdf': [],
              'image/png': [],
              'image/jpeg': [],
            }}
            hint={t('partner_page.repayment.fields.proof.hint')}
          />
          {!proofFile && (
            <p className="text-xs text-amber-700">
              {t('partner_page.repayment.fields.proof.required_hint')}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <Label>{t('partner_page.repayment.fields.notes.title')}</Label>
          <Textarea
            rows={3}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder={t('partner_page.repayment.fields.notes.placeholder')}
          />
        </div>
      </div>

      <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-xl border border-emerald-200 bg-white p-4">
        <Checkbox
          className="mt-0.5"
          checked={acknowledged}
          onCheckedChange={value => setAcknowledged(value === true)}
        />
        <span className="text-sm text-gray-700">
          {t('partner_page.repayment.fields.acknowledge.label')}
        </span>
      </label>

      <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
        <Button
          type="button"
          disabled={!canSubmit}
          onClick={handleSubmit}
          className="gap-2">
          {submitting ? (
            t('partner_page.repayment.actions.confirming')
          ) : (
            <>
              {t('partner_page.repayment.actions.confirm')}
              {terms && (
                <Price
                  amount={repaymentTotal}
                  currency={currency}
                  size="sm"
                  className="font-semibold"
                />
              )}
            </>
          )}
        </Button>
      </div>
    </section>
  );
}

/* ─────────────────────── recorded card ─────────────────────── */

/**
 * Read-only summary the lender renders after the repayment has been
 * stamped (i.e. `application.status === 'repaid'`). Closes the
 * lifecycle visually — the lender sees the gross they collected
 * back plus the funded / repaid timestamps so the page itself
 * doubles as a receipt.
 */
export function LenderRepaymentRecordedCard({
  view,
}: {
  view: FinancingLenderView;
}) {
  const { t, i18n } = useTranslation('m-financing');
  const { application } = view;
  const terms = application.myOffer.terms;
  const outcome = application.outcome;
  const repaymentTotal = terms ? terms.advancedAmount + terms.feeAmount : 0;
  const currency = application.invoice.currency as Currency;
  return (
    <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 shadow-sm sm:p-6">
      <div className="flex items-center gap-2">
        <CheckIcon className="h-5 w-5 text-emerald-600" />
        <h2 className="text-sm font-semibold text-gray-900">
          {t('partner_page.repayment.recorded.title')}
        </h2>
      </div>
      <p className="mt-1 text-sm text-gray-700">
        {t('partner_page.repayment.recorded.description')}
      </p>
      <dl className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
        {terms && (
          <SummaryRow
            label={t('partner_page.repayment.terms.total_received')}
            value={
              <Price
                amount={repaymentTotal}
                currency={currency}
                size="sm"
                className="font-semibold text-emerald-700"
              />
            }
          />
        )}
        {outcome?.fundedAt && (
          <SummaryRow
            label={t('partner_page.repayment.terms.funded_on')}
            value={formatDate(outcome.fundedAt, i18n.language)}
          />
        )}
        {outcome?.repaidAt && (
          <SummaryRow
            label={t('partner_page.repayment.terms.repaid_on')}
            value={formatDate(outcome.repaidAt, i18n.language)}
          />
        )}
      </dl>
    </section>
  );
}

function SummaryRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-md bg-white px-3 py-2">
      <dt className="text-[11px] uppercase tracking-wide text-gray-500">
        {label}
      </dt>
      <dd className="text-sm text-gray-900">{value}</dd>
    </div>
  );
}

/**
 * Read-only rejection banner the lender sees on the direct-record
 * path after a previous review round bounced. Surfaced from
 * `application.lastRejectionNotes` (only projected for the
 * accepted lender, only while a fresh receipt hasn't landed).
 * Sits above the direct-record panel and gives the lender context
 * for why they're back in this state.
 */
export function LenderRepaymentRejectionBanner({
  view,
}: {
  view: FinancingLenderView;
}) {
  const { t } = useTranslation('m-financing');
  const notes = view.application.lastRejectionNotes;
  if (!notes) return null;
  return (
    <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
      <AlertCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold text-amber-900">
          {t('partner_page.repayment.rejection_banner.title')}
        </h3>
        <p className="whitespace-pre-wrap text-sm text-amber-800">{notes}</p>
      </div>
    </div>
  );
}

/* ─────────────────────── helpers ─────────────────────── */

/**
 * Locked-terms card for the repayment surface. Lifted from the
 * funding panel's `LockedTermsCard` and tightened to surface the
 * fields the lender actually cares about at repayment time: the
 * "Total you'll collect" headline, the original advance, the fee
 * (lender's gross margin), and the funded / due dates so the
 * lender can sanity-check the receipt against the schedule.
 */
function LockedRepaymentTermsCard({
  terms,
  currency,
  fundedAt,
}: {
  terms: FinancingLenderView['application']['myOffer']['terms'];
  currency: Currency;
  fundedAt?: Date;
}) {
  const { t, i18n } = useTranslation('m-financing');
  const repaymentTotal = useMemo(
    () => (terms ? terms.advancedAmount + terms.feeAmount : 0),
    [terms],
  );
  if (!terms) {
    return (
      <div className="flex flex-col gap-2 rounded-xl border border-dashed border-gray-200 bg-gray-50 p-4">
        <FileTextIcon className="h-4 w-4 text-gray-400" />
        <p className="text-xs text-gray-500">
          {t('partner_page.repayment.terms.no_terms')}
        </p>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4">
      <h3 className="text-sm font-semibold text-gray-900">
        {t('partner_page.repayment.terms.title')}
      </h3>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <TermRow
          label={t('partner_page.repayment.terms.total_expected')}
          value={
            <Price
              amount={repaymentTotal}
              currency={currency}
              size="sm"
              className="font-semibold text-emerald-700"
            />
          }
          highlight
        />
        <TermRow
          label={t('partner_page.repayment.terms.advance')}
          value={
            <Price
              amount={terms.advancedAmount}
              currency={currency}
              size="sm"
              className="font-medium"
            />
          }
        />
        <TermRow
          label={t('partner_page.repayment.terms.fee')}
          value={
            <span>
              <Price
                amount={terms.feeAmount}
                currency={currency}
                size="sm"
                className="font-medium"
              />
              <span className="ms-1 text-gray-500">
                ({(terms.feePercentage * 100).toFixed(1)}%)
              </span>
            </span>
          }
        />
        <TermRow
          label={t('partner_page.repayment.terms.funded_on')}
          value={formatDate(fundedAt, i18n.language)}
        />
        <TermRow
          label={t('partner_page.repayment.terms.repayment_date')}
          value={formatDate(terms.repaymentDate, i18n.language)}
        />
      </dl>
    </div>
  );
}

function TermRow({
  label,
  value,
  highlight,
}: {
  label: string;
  value: ReactNode;
  highlight?: boolean;
}) {
  return (
    <div
      className={`flex flex-col gap-0.5 ${
        highlight ? 'col-span-2 rounded-md bg-emerald-50 px-2 py-1.5' : ''
      }`}>
      <dt className="text-[11px] uppercase tracking-wide text-gray-500">
        {label}
      </dt>
      <dd className="text-sm text-gray-900">{value}</dd>
    </div>
  );
}

/**
 * Format a Date/ISO string as a short calendar date in the active
 * locale. Returns `'—'` for falsy / unparseable inputs so the row
 * still renders consistently when an optional timestamp hasn't
 * landed yet.
 */
function formatDate(
  value: Date | string | undefined,
  lang: string,
): string {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(lang, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
